import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Users, Award, Lightbulb, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: <Lightbulb size={28} />,
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and creative problem-solving to deliver forward-thinking solutions.'
    },
    {
      icon: <Heart size={28} />,
      title: 'Integrity',
      description: 'Honesty and transparency guide every interaction, building trust with our clients and partners.'
    },
    {
      icon: <Award size={28} />,
      title: 'Efficiency',
      description: 'We optimize processes and deliver results that maximize value while minimizing waste.'
    }
  ];

  const stats = [
    { value: '50+', label: 'Projects Delivered' },
    { value: '30+', label: 'Happy Clients' },
    { value: '5+', label: 'Years Experience' },
    { value: '99%', label: 'Client Satisfaction' }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section
        data-testid="about-hero"
        className="relative py-24 lg:py-32 hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771011726574-60ab6028724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NjI5Mzg0fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-6">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Empowering Businesses Through Technology
            </h1>
            <p className="text-lg text-[#A1B0CC] leading-relaxed">
              Mohtech Cyber Solutions is a leading technology company based in Nairobi, Kenya, 
              dedicated to helping businesses thrive in the digital age.
            </p>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section data-testid="company-intro" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-[#A1B0CC] leading-relaxed">
                <p>
                  Founded with a vision to bridge the technology gap for African businesses, 
                  Mohtech Cyber Solutions has grown from a small startup to a trusted partner 
                  for companies across various industries.
                </p>
                <p>
                  Our journey began with a simple belief: every business, regardless of size, 
                  deserves access to world-class technology solutions. This belief continues 
                  to drive our mission today.
                </p>
                <p>
                  We specialize in building custom software solutions, fintech integrations, 
                  and automation systems that help businesses operate more efficiently and 
                  serve their customers better.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1540058404349-2e5fabf32d75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwc29mdHdhcmUlMjBkZXZlbG9wZXIlMjBjb2Rpbmd8ZW58MHx8fHwxNzc0NjI5Mzg1fDA&ixlib=rb-4.1.0&q=85"
                alt="Mohtech team working"
                className="rounded-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FF5E1A]/20 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section data-testid="mission-vision" className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#050B14] border border-white/5 rounded-2xl p-8 lg:p-12">
              <div className="w-16 h-16 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A] mb-6">
                <Target size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-[#A1B0CC] leading-relaxed">
                To empower businesses across Africa with innovative, reliable, and scalable 
                technology solutions that drive growth, efficiency, and digital transformation.
              </p>
            </div>
            <div className="bg-[#050B14] border border-white/5 rounded-2xl p-8 lg:p-12">
              <div className="w-16 h-16 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A] mb-6">
                <Eye size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-[#A1B0CC] leading-relaxed">
                To be the leading technology partner for businesses in East Africa, recognized 
                for our expertise in fintech, automation, and custom software development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="core-values" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              What We Stand For
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="text-center"
              >
                <div className="w-20 h-20 bg-[#FF5E1A]/10 rounded-2xl flex items-center justify-center text-[#FF5E1A] mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-[#A1B0CC] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="stats-section" className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                data-testid={`stat-${index}`}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-[#FF5E1A] mb-2">
                  {stat.value}
                </div>
                <div className="text-[#A1B0CC] text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section data-testid="team-section" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1710770563074-6d9cc0d3e338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwc29mdHdhcmUlMjBkZXZlbG9wZXIlMjBjb2Rpbmd8ZW58MHx8fHwxNzc0NjI5Mzg1fDA&ixlib=rb-4.1.0&q=85"
                alt="Developer at work"
                className="rounded-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Our Team
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Expert Developers, Local Insight
              </h2>
              <p className="text-[#A1B0CC] leading-relaxed mb-6">
                Our team consists of experienced software engineers, UX designers, and 
                project managers who are passionate about creating impactful technology 
                solutions. Based in Nairobi, we understand the unique challenges and 
                opportunities of the African market.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 bg-[#FF5E1A]/20 rounded-full border-2 border-[#050B14] flex items-center justify-center"
                    >
                      <Users size={16} className="text-[#FF5E1A]" />
                    </div>
                  ))}
                </div>
                <span className="text-[#A1B0CC] text-sm">10+ Team Members</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Let&apos;s Build Something Great Together
          </h2>
          <p className="text-[#A1B0CC] max-w-2xl mx-auto mb-8">
            Ready to transform your business with innovative technology solutions?
          </p>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full px-8 py-4 transition-all btn-glow"
          >
            Start a Conversation <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
